
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { File, FileText, Users } from "lucide-react";
import FrankApiSettings from "@/components/admin/FrankApiSettings";

interface OrganizationMember {
  id: string;
  full_name: string;
  organization: {
    id: string;
    name: string;
  };
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState<OrganizationMember | null>(null);

  useEffect(() => {
    const loadMemberData = async () => {
      try {
        const memberId = sessionStorage.getItem('organization_member_id');
        if (!memberId) {
          throw new Error("No member ID found");
        }

        const { data: memberResult, error: memberError } = await supabase
          .from('organization_members')
          .select(`
            id,
            full_name,
            organization:organization_id (
              id,
              name
            )
          `)
          .eq('id', memberId)
          .single();

        if (memberError) throw memberError;
        if (!memberResult) throw new Error("Member not found");

        setMemberData(memberResult);
      } catch (error) {
        console.error("Error loading member data:", error);
        toast.error("Failed to load member data");
      } finally {
        setLoading(false);
      }
    };

    loadMemberData();
  }, []);

  if (loading) {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-white group/design-root overflow-x-hidden pb-20 md:pb-0" style={{ fontFamily: 'Newsreader, "Noto Sans", sans-serif' }}>
        <div className="layout-container flex h-full grow flex-col">
          <div className="px-4 flex flex-1 justify-center py-5">
            <div className="flex items-center justify-center min-h-[50vh]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#111418]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!memberData) {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-white group/design-root overflow-x-hidden pb-20 md:pb-0" style={{ fontFamily: 'Newsreader, "Noto Sans", sans-serif' }}>
        <div className="layout-container flex h-full grow flex-col">
          <div className="px-4 flex flex-1 justify-center py-5">
            <div className="flex items-center justify-center min-h-[50vh]">
              <p className="text-[#637588]">Unable to load organization data</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const menuItems = [
    {
      title: "Documents",
      description: "Manage and organize your documents",
      icon: File,
      onClick: () => navigate('/manager/admin/documents')
    },
    {
      title: "Member Management", 
      description: "Oversee and manage your members",
      icon: Users,
      onClick: () => navigate('/manager/organization-users/' + encodeURIComponent(memberData.organization.name))
    },
    {
      title: "Article Management",
      description: "Manage and publish articles", 
      icon: FileText,
      onClick: () => navigate('/manager/articles')
    }
  ];

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-white group/design-root overflow-x-hidden pb-20 md:pb-0" style={{ fontFamily: 'Newsreader, "Noto Sans", sans-serif' }}>
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-3 sm:px-6 lg:px-8 flex flex-1 justify-center py-3 sm:py-5">
          <div className="layout-content-container flex flex-col max-w-6xl flex-1 w-full">
            <div className="flex flex-wrap justify-between gap-3 p-2 sm:p-4">
              <p className="text-[#111418] tracking-light text-2xl sm:text-[32px] font-bold leading-tight min-w-0">Admin Dashboard</p>
            </div>
            
            <p className="text-[#637588] text-sm px-2 sm:px-4 pb-4 sm:pb-6">
              Welcome, {memberData.full_name}. Manage {memberData.organization.name} here.
            </p>

            <h2 className="text-[#111418] text-lg sm:text-[22px] font-bold leading-tight tracking-[-0.015em] px-2 sm:px-4 pb-2 sm:pb-3 pt-3 sm:pt-5">Manage</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-2 sm:p-4">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    onClick={item.onClick}
                    className="flex gap-3 rounded-lg border-[0.5px] border-[#dce0e5] bg-white p-3 sm:p-4 flex-col cursor-pointer hover:shadow-md transition-all duration-200 hover:border-[#2F80ED] w-full"
                  >
                    <div className="text-[#111418]">
                      <Icon size={20} className="sm:w-6 sm:h-6" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <h2 className="text-[#111418] text-sm sm:text-base font-bold leading-tight">{item.title}</h2>
                      <p className="text-[#637588] text-xs sm:text-sm font-normal leading-normal">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <h2 className="text-[#111418] text-lg sm:text-[22px] font-bold leading-tight tracking-[-0.015em] px-2 sm:px-4 pb-2 sm:pb-3 pt-6 sm:pt-8">Configuration</h2>
            
            <div className="p-2 sm:p-4">
              <div className="flex flex-1 gap-3 rounded-lg border-[0.5px] border-[#dce0e5] bg-white p-3 sm:p-6 flex-col">
                <div className="flex flex-col gap-1 mb-3 sm:mb-4">
                  <h2 className="text-[#111418] text-sm sm:text-base font-bold leading-tight">Frank AI Configuration</h2>
                  <p className="text-[#637588] text-xs sm:text-sm font-normal leading-normal">Configure AI assistant settings</p>
                </div>
                <FrankApiSettings />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
